// Task #3062 — Thin client helpers that proxy the auth-modal surface
// (login, signup, magic-link / OTP request, password reset) through our
// API server's /auth/* endpoints instead of calling Supabase directly.
// The server applies per-IP + per-email sliding-window rate limits in
// front of every call so abuse is scoped to the actor and so all four
// paths return a consistent 429 + Retry-After envelope.
//
// Each helper returns a plain object — no thrown errors for transport
// or 4xx responses — so the AuthProvider callbacks that wrap them can
// keep their existing `{ error, ... }` return shape.

import type { Session, User } from "@supabase/supabase-js";
import type { OtpJourneyMetadata } from "@/lib/auth/otp-journey";
import { getTurnstileToken, TURNSTILE_HEADER } from "./turnstile";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export interface ProxyRateLimited {
  rateLimited: true;
  retryAfter: number;
}

interface SessionShape {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface ProxyLoginResult {
  rateLimited?: false;
  session?: SessionShape;
  error?: string;
  // Task #3890 (S-M7) — the server collapses every login failure to one
  // generic message and conveys the only signal the client needs as a
  // machine code. `email_not_confirmed` is the post-signup user who needs
  // the resend-verification flow; no other distinguishing detail is sent.
  code?: string;
  retryAfter?: number;
}

export interface ProxySignupResult {
  rateLimited?: false;
  user: User | null;
  session: Session | null;
  error?: string;
  code?: string;
  retryAfter?: number;
}

export interface ProxyOkResult {
  rateLimited?: false;
  error?: string;
  retryAfter?: number;
  // "transport" = the API server was unreachable or returned 5xx (cold-
  // start during workspace warm-up, dev-server restart, proxy hiccup,
  // DNS/TLS error). Callers should surface a "we couldn't reach the
  // server, try again in a moment" message instead of the ambiguous
  // "Couldn't send code" — and they should NOT lock the user into a
  // rate-limit-style cooldown UI because nothing was actually sent.
  // "supabase" = our server reached Supabase and Supabase returned an
  // error; `error` carries Supabase's reason.
  // "send_failed" (Task #4984) = our server reached Supabase but the email
  // genuinely could NOT be sent (config/provider error). Distinct from
  // "transport" — the request DID leave the box and reach Supabase — so the
  // caller shows a dedicated "couldn't send the reset email" message instead
  // of the misleading "couldn't reach the server" transport copy.
  // "unavailable" = the server's rate-limit store is temporarily down and the
  // fail-closed limiter couldn't be evaluated. NOT a real rate limit — the
  // caller must show "temporarily unavailable, try again shortly" instead of
  // a cooldown lockout.
  code?: "transport" | "supabase" | "send_failed" | "unavailable";
}

type WithRateLimited<T> = T | ProxyRateLimited;

async function postJson(path: string, body: Record<string, unknown>): Promise<{
  ok: boolean;
  status: number;
  retryAfter: number;
  data: Record<string, unknown>;
}> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network / DNS / TLS failure. Surface as a non-rate-limited error
    // so the caller's existing "couldn't sign in" copy still renders.
    return {
      ok: false,
      status: 0,
      retryAfter: 0,
      data: { error: err instanceof Error ? err.message : "Network error" },
    };
  }
  const data = await res.json().catch(() => ({} as Record<string, unknown>));
  const headerRetry = Number(res.headers.get("Retry-After") || 0);
  const retryAfter = Number((data as { retryAfter?: unknown }).retryAfter) || headerRetry || 60;
  return { ok: res.ok, status: res.status, retryAfter, data };
}

/**
 * Build the user-facing Error returned from AuthProvider callbacks when a
 * proxied auth call is rate-limited. Carries the seconds-until-retry as
 * `retryAfter` so callers (the auth modal) can render a cooldown without
 * having to parse the message string.
 */
export function rateLimitedAuthError(retryAfterSec: number): Error & { retryAfter: number; code: string } {
  const e = new Error(
    `Too many attempts. Please wait ${Math.max(1, Math.ceil(retryAfterSec))} seconds and try again.`,
  ) as Error & { retryAfter: number; code: string };
  e.retryAfter = retryAfterSec;
  e.code = "rate_limited";
  return e;
}

export async function proxyAuthLogin(email: string, password: string): Promise<WithRateLimited<ProxyLoginResult>> {
  const { ok, status, retryAfter, data } = await postJson("/auth/login", { email, password });
  if (status === 429) return { rateLimited: true, retryAfter };
  if (!ok) return { error: (data.error as string) || "Sign-in failed", code: (data.code as string) || undefined };
  return { session: data.session as SessionShape | undefined };
}

export async function proxyAuthSignup(
  email: string,
  password: string,
  metadata?: Record<string, string>,
  emailRedirectTo?: string,
): Promise<WithRateLimited<ProxySignupResult>> {
  // Task #3228 — attach a bot-challenge token (null when the feature is
  // off) so the server's enforceTurnstile gate passes.
  const turnstileToken = await getTurnstileToken();
  const { ok, status, retryAfter, data } = await postJson("/auth/signup", {
    email,
    password,
    metadata,
    emailRedirectTo,
    [TURNSTILE_HEADER]: turnstileToken ?? undefined,
  });
  if (status === 429) return { rateLimited: true, retryAfter };
  if (!ok) {
    return {
      user: null,
      session: null,
      error: (data.error as string) || "Sign-up failed",
      code: (data.code as string) || undefined,
    };
  }
  return {
    user: (data.user as User | null) ?? null,
    session: (data.session as Session | null) ?? null,
  };
}

// status === 0 means fetch threw (network/DNS/TLS); 5xx means our API
// server (or the upstream proxy) is unreachable or erroring out before
// our handler ran. In either case nothing was actually sent to
// Supabase, so we tag the result with code:"transport" and let the
// caller surface a transient-error message instead of the generic
// "Couldn't send code" — which sounds like Supabase rejected the
// address when in reality the request never left the box.
function isTransportFailure(status: number): boolean {
  return status === 0 || status >= 500;
}

export async function proxyAuthRequestOtp(
  email: string,
  emailRedirectTo?: string,
  opts?: { force?: boolean; journey?: OtpJourneyMetadata; testDelivery?: boolean },
): Promise<WithRateLimited<ProxyOkResult & { alreadySent?: boolean; retryAfterSec?: number; otpJourneyProof?: string; otpTestReceipt?: string }>> {
  // Task #3228 — attach a bot-challenge token (null when the feature is
  // off) so the server's enforceTurnstile gate passes.
  const turnstileToken = await getTurnstileToken();
  const { ok, status, retryAfter, data } = await postJson("/auth/request-otp", {
    email,
    emailRedirectTo,
    // Task #6311 — explicit "Send a new code" clicks force a fresh mint;
    // everything else is subject to the server's already-sent dedupe.
    force: opts?.force === true ? true : undefined,
    otpTest: opts?.testDelivery === true ? true : undefined,
    ...opts?.journey,
    [TURNSTILE_HEADER]: turnstileToken ?? undefined,
  });
  if (status === 429) return { rateLimited: true, retryAfter };
  if (!ok) {
    return {
      error: (data.error as string) || "Couldn't send code",
      // A 503 from the fail-closed rate-limit guard is not a generic
      // transport failure and must not be mistaken for an OTP provider error
      // or a real rate limit. Preserve the server's explicit classification.
      code: data.code === "unavailable"
        ? "unavailable"
        : isTransportFailure(status) ? "transport" : "supabase",
    };
  }
  // Task #6311 — the server skipped the Supabase call because a code was
  // sent moments ago and is still valid. Surface it so the modal can render
  // guidance instead of pretending a new email went out.
  if (data.alreadySent === true) {
    return {
      alreadySent: true,
      retryAfterSec: typeof data.retryAfterSec === "number" ? data.retryAfterSec : undefined,
      otpJourneyProof: typeof data.otpJourneyProof === "string" ? data.otpJourneyProof : undefined,
      otpTestReceipt: typeof data.otpTestReceipt === "string" ? data.otpTestReceipt : undefined,
    };
  }
  return {
    otpJourneyProof: typeof data.otpJourneyProof === "string" ? data.otpJourneyProof : undefined,
    otpTestReceipt: typeof data.otpTestReceipt === "string" ? data.otpTestReceipt : undefined,
  };
}

export async function proxyAuthTestOtp(
  receipt: string,
): Promise<{ code: string; magicLinkTokenHash: string } | null> {
  try {
    const response = await fetch(
      `${API_BASE}/auth/test-otp?receipt=${encodeURIComponent(receipt)}`,
      { headers: { Accept: "application/json" } },
    );
    if (!response.ok) return null;
    const data = (await response.json()) as { code?: unknown; magicLinkTokenHash?: unknown };
    return typeof data.code === "string" &&
      /^\d{6}$/.test(data.code) &&
      typeof data.magicLinkTokenHash === "string" &&
      data.magicLinkTokenHash.length > 0
      ? { code: data.code, magicLinkTokenHash: data.magicLinkTokenHash }
      : null;
  } catch {
    return null;
  }
}

export async function reportOtpVerificationComplete(
  accessToken: string,
  journey: OtpJourneyMetadata,
  journeyProof: string,
): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/otp-verification-complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ ...journey, otp_journey_proof: journeyProof }),
    });
  } catch {
    // Funnel observability cannot interrupt the verified renter's redirect.
  }
}

export async function proxyAuthPasswordReset(
  email: string,
  redirectTo?: string,
): Promise<WithRateLimited<ProxyOkResult>> {
  // Task #5579 — route through the admin-minted recovery bypass so the user's
  // FIRST "Forgot password?" click always sends, even when the Airbnb-style
  // sign-in just fired an OTP email (which would otherwise arm Supabase's
  // per-recipient send interval and throttle resetPasswordForEmail). The
  // bypass returns the SAME response envelope (429 / send_failed / transport /
  // ok) as the legacy /auth/password-reset route, so the handling below is
  // unchanged.
  const { ok, status, retryAfter, data } = await postJson("/auth/password-reset-bypass", { email, redirectTo });
  if (status === 429) return { rateLimited: true, retryAfter };
  if (!ok) {
    // Task #4984 — the server emits an explicit `send_failed` code when the
    // recovery email genuinely could not be sent (config/provider error). It
    // rides on a 5xx, so check the code FIRST — otherwise isTransportFailure
    // would misclassify it as a "couldn't reach the server" transport error.
    // Task #6388 — "unavailable" also rides on a 5xx (503), so it must be
    // checked before the transport fallback too.
    const code: NonNullable<ProxyOkResult["code"]> =
      data.code === "send_failed" ? "send_failed"
        : data.code === "unavailable" ? "unavailable"
          : isTransportFailure(status) ? "transport" : "supabase";
    return {
      error: (data.error as string) || "Couldn't send reset email",
      code,
    };
  }
  return {};
}
